import { EmailSearchSchema, PasswordResetSchema } from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	createFileRoute,
	Link,
	redirect,
	useParams,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { AuthPage } from "@/components/auth/auth-page";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input-password";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/reset-password/$token")({
	component: ResetPassword,
	beforeLoad: ({ search }) => {
		const normalizedSearch = {
			...search,
			email:
				typeof search.email === "string" && search.email.trim() === ""
					? undefined
					: search.email,
		};
		const result = EmailSearchSchema.safeParse(normalizedSearch);
		if (!result.success) {
			throw redirect({
				to: "/auth/reset-password",
			});
		}
	},
	head: () =>
		seo({
			title: "Reset Password",
			description: "Reset your password",
			keywords: "reset, password, security",
		}),
	validateSearch: (search: Record<string, unknown>) => {
		const normalizedSearch = {
			...search,
			email:
				typeof search.email === "string" && search.email.trim() === ""
					? undefined
					: search.email,
		};
		const result = EmailSearchSchema.safeParse(normalizedSearch);
		if (result.success) {
			return result.data;
		}
		return { email: undefined };
	},
});

export function ResetPassword() {
	const router = useRouter();
	const { token } = useParams({ from: "/auth/reset-password/$token" });
	const search = useSearch({
		strict: false,
	});

	const form = useForm<z.infer<typeof PasswordResetSchema>>({
		resolver: zodResolver(PasswordResetSchema),
		defaultValues: {
			email: search.email ?? "",
			token: token ?? "",
			password: "",
			confirmPassword: "",
		},
	});

	const onSubmit = async (data: z.infer<typeof PasswordResetSchema>) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.authResetPasswordCreate(data.token, {
					new_password: data.password,
				}),
			);
			toast.success("Password reset successfully");
			router.navigate({ to: "/auth/login" });
		} catch (error) {
			toast.error("Password reset failed", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<AuthPage
			title="Choose a new password"
			description="Paste the token from your email and set a new password."
			variant="comfortable"
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Email</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="email"
										required
										autoComplete="email"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="token"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Reset token</FormLabel>
								<FormControl>
									<Input {...field} type="text" required autoComplete="off" />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem>
									<FormLabel>New password</FormLabel>
									<FormControl>
										<PasswordInput
											{...field}
											required
											autoComplete="new-password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="confirmPassword"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Confirm password</FormLabel>
									<FormControl>
										<PasswordInput
											{...field}
											required
											autoComplete="new-password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<Button type="submit" className="h-11 w-full cursor-pointer">
						Update password
					</Button>
				</form>
			</Form>

			<p className="mt-8 text-center text-base text-muted-foreground">
				<Link
					to="/auth/login"
					className="font-medium text-foreground underline-offset-4 hover:underline"
				>
					Back to sign in
				</Link>
			</p>
		</AuthPage>
	);
}
